<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import VtexAPI from 'dashboard/api/integrations/vtex';
import VTEXOrderItem from './VTEXOrderItem.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
  conversationId: {
    type: [Number, String],
    default: '',
  },
  inboxId: {
    type: [Number, String],
    default: '',
  },
});

const { t } = useI18n();
const store = useStore();

const contact = useFunctionGetter('contacts/getContact', props.contactId);
const integration = useFunctionGetter('integrations/getIntegration', 'vtex');
const conversation = useFunctionGetter(
  'getConversationById',
  props.conversationId
);

const settings = computed(() => integration.value?.hooks?.[0]?.settings || {});
const currentInbox = computed(() =>
  store.getters['inboxes/getInbox'](props.inboxId)
);
const resolvedInboxId = computed(() => {
  const inboxId = props.inboxId;
  const exactOverride = settings.value.inbox_overrides?.[`${inboxId}`];
  if (!inboxId || exactOverride) {
    return inboxId;
  }

  const instagramId = currentInbox.value.instagram_id;
  if (!instagramId) {
    return inboxId;
  }

  if (currentInbox.value.channel_type === 'Channel::Instagram') {
    return (
      store.getters['inboxes/getFacebookInboxByInstagramId'](instagramId)?.id ||
      inboxId
    );
  }

  if (currentInbox.value.channel_type === 'Channel::FacebookPage') {
    return (
      store.getters['inboxes/getInstagramInboxByInstagramId'](instagramId)
        ?.id || inboxId
    );
  }

  return inboxId;
});
const inboxOverride = computed(
  () => settings.value.inbox_overrides?.[`${resolvedInboxId.value}`] || {}
);
const inboxEnabled = computed(() => {
  if (!Object.keys(inboxOverride.value || {}).length) {
    return true;
  }

  return inboxOverride.value.enabled === true;
});
const modules = computed(() => {
  const rawModules = settings.value.modules || {};
  const overrideModules = inboxOverride.value.modules || {};

  return {
    customer_lookup:
      overrideModules.customer_lookup ?? rawModules.customer_lookup ?? true,
    orders_sidebar:
      overrideModules.orders_sidebar ?? rawModules.orders_sidebar ?? true,
    tracking_sidebar:
      overrideModules.tracking_sidebar ?? rawModules.tracking_sidebar ?? true,
    payments_sidebar:
      overrideModules.payments_sidebar ?? rawModules.payments_sidebar ?? true,
    returns_sidebar:
      overrideModules.returns_sidebar ?? rawModules.returns_sidebar ?? true,
    catalog_search:
      overrideModules.catalog_search ?? rawModules.catalog_search ?? true,
    price_lookup:
      overrideModules.price_lookup ?? rawModules.price_lookup ?? true,
    inventory_lookup:
      overrideModules.inventory_lookup ?? rawModules.inventory_lookup ?? true,
    draft_orders:
      overrideModules.draft_orders ?? rawModules.draft_orders ?? true,
    checkout_approval:
      overrideModules.checkout_approval ?? rawModules.checkout_approval ?? true,
  };
});
const capabilities = computed(() => {
  const rawCapabilities = settings.value.capabilities || {};
  const customerLookup = rawCapabilities.customer_lookup === true;
  const ordersSidebar = rawCapabilities.orders_sidebar === true;
  const catalogSearch = rawCapabilities.catalog_search === true;

  return {
    customer_lookup: customerLookup,
    orders_sidebar: ordersSidebar,
    tracking_sidebar: rawCapabilities.tracking_sidebar ?? ordersSidebar,
    payments_sidebar: rawCapabilities.payments_sidebar ?? ordersSidebar,
    returns_sidebar: rawCapabilities.returns_sidebar ?? ordersSidebar,
    catalog_search: catalogSearch,
    price_lookup: rawCapabilities.price_lookup ?? catalogSearch,
    inventory_lookup: rawCapabilities.inventory_lookup ?? catalogSearch,
    draft_orders:
      rawCapabilities.draft_orders ?? (customerLookup && catalogSearch),
    checkout_approval:
      rawCapabilities.checkout_approval ?? (customerLookup && catalogSearch),
  };
});
const canIdentifyContact = computed(
  () => !!contact.value?.email || !!contact.value?.phone_number
);
const canSearchCustomer = computed(
  () => canIdentifyContact.value && modules.value.customer_lookup
);
const canLoadOrders = computed(
  () => canIdentifyContact.value && modules.value.orders_sidebar
);
const canManageDrafts = computed(
  () =>
    inboxEnabled.value &&
    canIdentifyContact.value &&
    modules.value.draft_orders &&
    capabilities.value.draft_orders
);

const loading = ref(false);
const error = ref('');
const customer = ref(null);
const orders = ref([]);
const drafts = ref([]);
const orderCount = computed(() => orders.value.length);
const hasCustomer = computed(() => !!customer.value?.id);

const draftSearchQuery = ref('');
const draftSearchResults = ref([]);
const draftSearchError = ref('');
const draftItems = ref([]);
const draftAddress = ref({
  receiver_name: '',
  street: '',
  number: '',
  neighborhood: '',
  city: '',
  state: '',
  postal_code: '',
  country: 'ECU',
  complement: '',
});
const draftLogistics = ref({
  selected_sla: '',
  courier: '',
  shipping_estimate: '',
});
const draftNotes = ref('');
const activeDraftId = ref(null);
const isSearchingDraftProducts = ref(false);
const isSavingDraft = ref(false);
const isApprovingDraft = ref(false);
const isCreatingCustomer = ref(false);
const isConfirmingDraft = ref(false);
const isDraftComposerOpen = ref(false);
const isDraftListOpen = ref(false);
const isOrdersOpen = ref(false);
const draftComposerDialogRef = ref(null);
const customerForm = ref({
  name: '',
  email: '',
  phone: '',
  document: '',
});

const fallbackError = computed(() => t('CONVERSATION_SIDEBAR.VTEX.ERROR'));
const displayError = computed(() => error.value);
const displayDraftSearchError = computed(() => draftSearchError.value);
const saveDraftLabel = computed(() =>
  activeDraftId.value
    ? t('CONVERSATION_SIDEBAR.VTEX.UPDATE_DRAFT')
    : t('CONVERSATION_SIDEBAR.VTEX.SAVE_DRAFT')
);
const draftCount = computed(() => drafts.value.length);
const draftItemCount = computed(() =>
  draftItems.value.reduce((sum, item) => sum + Number(item.quantity || 0), 0)
);
const visibleOrders = computed(() =>
  isOrdersOpen.value ? orders.value : orders.value.slice(0, 1)
);
const draftTotal = computed(() =>
  draftItems.value.reduce(
    (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0),
    0
  )
);

const formatCurrency = value =>
  new Intl.NumberFormat('en', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatSkuLabel = value =>
  t('INTEGRATION_SETTINGS.VTEX.SEARCH.LABEL_VALUE', {
    label: t('INTEGRATION_SETTINGS.VTEX.SEARCH.SKU'),
    value,
  });

const formatUnitPrice = value =>
  t('CONVERSATION_SIDEBAR.VTEX.UNIT_PRICE', {
    value: formatCurrency(value),
  });

const normalizeDraftItem = item => ({
  sku_id: item?.sku_id || item?.skuId || '',
  ref_id: item?.ref_id || item?.refId || '',
  name: item?.name || '',
  quantity: Number(item?.quantity || 1),
  price: Number(item?.price || 0),
  stock_quantity: item?.stock_quantity ?? item?.stockQuantity,
  seller_id: item?.seller_id || item?.sellerId || '',
});

const conversationCartItems = computed(() => {
  const items = conversation.value?.custom_attributes?.captain_cart_items || [];
  return items
    .map(normalizeDraftItem)
    .filter(item => `${item.sku_id}`.trim().length);
});

const openDraftComposer = () => {
  draftComposerDialogRef.value?.open();
};

const resetDraftForm = () => {
  activeDraftId.value = null;
  isDraftComposerOpen.value = true;
  draftSearchQuery.value = '';
  draftSearchResults.value = [];
  draftSearchError.value = '';
  draftItems.value = conversationCartItems.value.map(item => ({ ...item }));
  draftAddress.value = {
    receiver_name: customer.value?.name || contact.value?.name || '',
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'ECU',
    complement: '',
  };
  draftLogistics.value = {
    selected_sla: '',
    courier: '',
    shipping_estimate: '',
  };
  draftNotes.value = '';
  customerForm.value = {
    name: customer.value?.name || contact.value?.name || '',
    email: customer.value?.email || contact.value?.email || '',
    phone: customer.value?.phone || contact.value?.phone_number || '',
    document: customer.value?.document || '',
  };
};

const applyDraftToForm = draft => {
  activeDraftId.value = draft.id;
  isDraftComposerOpen.value = true;
  draftItems.value = (draft.items || []).map(item => ({
    ...item,
    quantity: Number(item.quantity || 1),
  }));
  draftAddress.value = {
    receiver_name: draft.address?.receiver_name || draft.address?.name || '',
    street: draft.address?.street || '',
    number: draft.address?.number || '',
    neighborhood: draft.address?.neighborhood || '',
    city: draft.address?.city || '',
    state: draft.address?.state || '',
    postal_code: draft.address?.postal_code || '',
    country: draft.address?.country || 'ECU',
    complement: draft.address?.complement || '',
  };
  draftLogistics.value = {
    selected_sla: draft.logistics?.selected_sla || '',
    courier: draft.logistics?.courier || '',
    shipping_estimate: draft.logistics?.shipping_estimate || '',
  };
  draftNotes.value = draft.notes || '';
  openDraftComposer();
};

const upsertDraft = draft => {
  const index = drafts.value.findIndex(item => item.id === draft.id);
  if (index === -1) {
    drafts.value = [draft, ...drafts.value];
    return;
  }

  const nextDrafts = [...drafts.value];
  nextDrafts.splice(index, 1, draft);
  drafts.value = nextDrafts;
};

const loadDrafts = async () => {
  if (
    !canIdentifyContact.value ||
    !modules.value.draft_orders ||
    !inboxEnabled.value
  ) {
    drafts.value = [];
    return;
  }

  const response = await VtexAPI.getDraftOrders(
    props.contactId,
    resolvedInboxId.value
  );
  drafts.value = response.data.drafts || [];
};

const loadPanel = async () => {
  loading.value = true;
  error.value = '';

  try {
    const requests = [];
    if (canSearchCustomer.value) {
      requests.push(
        VtexAPI.getCustomer(props.contactId, resolvedInboxId.value)
      );
    } else {
      requests.push(Promise.resolve({ data: { customer: null } }));
    }

    if (canLoadOrders.value) {
      requests.push(VtexAPI.getOrders(props.contactId, resolvedInboxId.value));
    } else {
      requests.push(Promise.resolve({ data: { orders: [] } }));
    }

    const [customerResponse, ordersResponse] = await Promise.all(requests);
    customer.value = customerResponse.data.customer;
    orders.value = customerResponse.data.customer
      ? ordersResponse.data.orders || []
      : [];

    await loadDrafts();

    if (!activeDraftId.value) {
      resetDraftForm();
    }
  } catch (e) {
    error.value = e.response?.data?.error || fallbackError.value;
  } finally {
    loading.value = false;
  }
};

const createCustomer = async () => {
  if (isCreatingCustomer.value) {
    return;
  }

  isCreatingCustomer.value = true;
  draftSearchError.value = '';

  try {
    const response = await VtexAPI.createCustomer(
      props.contactId,
      resolvedInboxId.value,
      customerForm.value
    );
    customer.value = response.data.customer;
    await loadDrafts();
    resetDraftForm();
  } catch (e) {
    draftSearchError.value = e.response?.data?.error || fallbackError.value;
  } finally {
    isCreatingCustomer.value = false;
  }
};

const searchDraftProducts = async () => {
  if (isSearchingDraftProducts.value) {
    return;
  }

  draftSearchError.value = '';

  if (!draftSearchQuery.value.trim()) {
    draftSearchError.value = t(
      'CONVERSATION_SIDEBAR.VTEX.DRAFT_PRODUCTS_EMPTY'
    );
    return;
  }

  isSearchingDraftProducts.value = true;
  try {
    const response = await VtexAPI.searchProducts(
      draftSearchQuery.value.trim(),
      resolvedInboxId.value
    );
    draftSearchResults.value = response.data.products || [];
  } catch (e) {
    draftSearchError.value = e.response?.data?.error || fallbackError.value;
  } finally {
    isSearchingDraftProducts.value = false;
  }
};

const addDraftItem = product => {
  const existingItem = draftItems.value.find(
    item => `${item.sku_id}` === `${product.sku_id}`
  );

  if (existingItem) {
    existingItem.quantity += 1;
    return;
  }

  draftItems.value = [
    ...draftItems.value,
    {
      sku_id: product.sku_id,
      ref_id: product.ref_id,
      name: product.variant_name || product.name,
      quantity: 1,
      price: product.price,
      stock_quantity: product.stock_quantity ?? product.availability,
      seller_id: product.seller_id,
    },
  ];
};

const updateItemQuantity = (skuId, nextQuantity) => {
  draftItems.value = draftItems.value
    .map(item =>
      `${item.sku_id}` === `${skuId}`
        ? { ...item, quantity: Math.max(Number(nextQuantity || 1), 1) }
        : item
    )
    .filter(item => Number(item.quantity) > 0);
};

const changeItemQuantity = (skuId, delta) => {
  const currentItem = draftItems.value.find(
    item => `${item.sku_id}` === `${skuId}`
  );
  if (!currentItem) {
    return;
  }

  updateItemQuantity(skuId, Number(currentItem.quantity || 1) + delta);
};

const removeItem = skuId => {
  draftItems.value = draftItems.value.filter(
    item => `${item.sku_id}` !== `${skuId}`
  );
};

const draftPayload = () => ({
  contact_id: props.contactId,
  inbox_id: resolvedInboxId.value,
  draft_order: {
    conversation_id: props.conversationId,
    notes: draftNotes.value,
    items: draftItems.value.map(item => ({
      sku_id: item.sku_id,
      ref_id: item.ref_id,
      name: item.name,
      quantity: item.quantity,
      price: item.price,
      stock_quantity: item.stock_quantity,
      seller_id: item.seller_id,
    })),
    address: draftAddress.value,
    logistics: draftLogistics.value,
  },
});

const saveDraft = async () => {
  if (isSavingDraft.value) {
    return;
  }

  if (!draftItems.value.length) {
    draftSearchError.value = t('CONVERSATION_SIDEBAR.VTEX.DRAFT_EMPTY');
    return;
  }

  isSavingDraft.value = true;
  draftSearchError.value = '';

  try {
    const response = activeDraftId.value
      ? await VtexAPI.updateDraftOrder(activeDraftId.value, draftPayload())
      : await VtexAPI.createDraftOrder(draftPayload());

    const draft = response.data.draft;
    activeDraftId.value = draft.id;
    upsertDraft(draft);
  } catch (e) {
    draftSearchError.value = e.response?.data?.error || fallbackError.value;
  } finally {
    isSavingDraft.value = false;
  }
};

const approveDraft = async draftId => {
  const targetDraftId = draftId || activeDraftId.value;
  if (!targetDraftId || isApprovingDraft.value) {
    return;
  }

  isApprovingDraft.value = true;
  draftSearchError.value = '';

  try {
    const response = await VtexAPI.approveDraftOrder(targetDraftId);
    upsertDraft(response.data.draft);
    activeDraftId.value = response.data.draft.id;
  } catch (e) {
    draftSearchError.value = e.response?.data?.error || fallbackError.value;
  } finally {
    isApprovingDraft.value = false;
  }
};

const confirmDraft = async draftId => {
  if (isConfirmingDraft.value) {
    return;
  }

  isConfirmingDraft.value = true;
  try {
    const response = await VtexAPI.confirmDraftOrder(draftId, {});
    upsertDraft(response.data.draft);
  } catch (e) {
    draftSearchError.value = e.response?.data?.error || fallbackError.value;
  } finally {
    isConfirmingDraft.value = false;
  }
};

const deleteDraft = async draftId => {
  if (!draftId) {
    return;
  }

  try {
    await VtexAPI.deleteDraftOrder(draftId);
    drafts.value = drafts.value.filter(draft => draft.id !== draftId);

    if (activeDraftId.value === draftId) {
      resetDraftForm();
    }
  } catch (e) {
    draftSearchError.value = e.response?.data?.error || fallbackError.value;
  }
};

watch(
  [
    () => props.contactId,
    resolvedInboxId,
    () => contact.value?.email,
    () => contact.value?.phone_number,
    modules,
    inboxEnabled,
  ],
  () => {
    if (canIdentifyContact.value && inboxEnabled.value) {
      loadPanel();
    } else {
      customer.value = null;
      orders.value = [];
      drafts.value = [];
    }
  },
  { immediate: true }
);

watch(
  conversationCartItems,
  items => {
    if (!activeDraftId.value) {
      draftItems.value = items.map(item => ({ ...item }));
    }
  },
  { deep: true }
);
</script>

<template>
  <div class="px-3 py-3 text-n-slate-12">
    <div
      class="rounded-2xl border border-n-strong bg-n-solid-2 shadow-sm overflow-hidden"
    >
      <div class="px-4 py-4 border-b border-n-weak bg-n-solid-1">
        <div class="flex flex-col gap-3">
          <div>
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('CONVERSATION_SIDEBAR.VTEX.PANEL_TITLE') }}
            </h3>
            <p class="mt-1 text-[11px] text-n-slate-11 leading-4">
              {{ $t('CONVERSATION_SIDEBAR.VTEX.PANEL_SUBTITLE') }}
            </p>
          </div>

          <div class="grid grid-cols-2 gap-2">
            <div class="rounded-xl border border-n-weak bg-n-card px-2.5 py-2">
              <p
                :title="$t('CONVERSATION_SIDEBAR.VTEX.ORDERS_TITLE')"
                class="truncate text-[10px] uppercase tracking-[0.04em] text-n-slate-10"
              >
                {{ $t('CONVERSATION_SIDEBAR.VTEX.ORDERS_TITLE') }}
              </p>
              <p class="mt-1 text-base font-semibold text-n-slate-12">
                {{ orderCount }}
              </p>
            </div>
            <div class="rounded-xl border border-n-weak bg-n-card px-2.5 py-2">
              <p
                :title="$t('CONVERSATION_SIDEBAR.VTEX.DRAFTS_TITLE')"
                class="truncate text-[10px] uppercase tracking-[0.04em] text-n-slate-10"
              >
                {{ $t('CONVERSATION_SIDEBAR.VTEX.DRAFTS_TITLE') }}
              </p>
              <p class="mt-1 text-base font-semibold text-n-slate-12">
                {{ draftCount }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="p-4">
        <div
          v-if="!canIdentifyContact"
          class="rounded-xl border border-dashed border-n-strong bg-n-alpha-2 px-4 py-6 text-center text-sm text-n-slate-11"
        >
          {{ $t('CONVERSATION_SIDEBAR.VTEX.NO_CONTACT_IDENTIFIER') }}
        </div>
        <div
          v-else-if="loading"
          class="flex flex-col items-center justify-center gap-3 py-10"
        >
          <Spinner :size="32" class="text-n-brand" />
          <p class="text-sm text-n-slate-11">
            {{ $t('CONVERSATION_SIDEBAR.VTEX.LOADING') }}
          </p>
        </div>
        <div
          v-else-if="error"
          class="rounded-xl border border-n-ruby-4 bg-n-ruby-2 px-4 py-4 text-sm text-n-ruby-11"
        >
          {{ displayError }}
        </div>
        <div v-else class="flex flex-col gap-4">
          <div
            v-if="modules.customer_lookup"
            class="rounded-2xl border border-n-weak bg-n-card p-3"
          >
            <div class="flex items-start justify-between gap-3">
              <div>
                <p
                  class="text-[10px] font-semibold uppercase tracking-[0.06em] text-n-slate-10"
                >
                  {{ $t('CONVERSATION_SIDEBAR.VTEX.CUSTOMER_EYEBROW') }}
                </p>
                <h4 class="mt-1 text-sm font-semibold text-n-slate-12">
                  {{ $t('CONVERSATION_SIDEBAR.VTEX.CUSTOMER_TITLE') }}
                </h4>
              </div>
            </div>

            <div v-if="customer" class="mt-3 flex flex-col gap-3">
              <div class="flex items-center gap-3">
                <div
                  class="h-10 w-10 rounded-full bg-n-brand text-white flex items-center justify-center text-sm font-semibold shrink-0"
                >
                  {{
                    (customer.name || customer.email || customer.phone || 'V')
                      .slice(0, 1)
                      .toUpperCase()
                  }}
                </div>
                <div class="min-w-0">
                  <p
                    :title="customer.name || customer.email || customer.phone"
                    class="text-sm font-semibold text-n-slate-12 truncate"
                  >
                    {{ customer.name || customer.email || customer.phone }}
                  </p>
                  <p
                    :title="customer.email || customer.phone"
                    class="text-xs text-n-slate-11 truncate"
                  >
                    {{ customer.email || customer.phone }}
                  </p>
                </div>
              </div>

              <div class="flex flex-wrap gap-2">
                <span
                  v-if="customer.orders_count !== undefined"
                  class="rounded-full bg-n-alpha-2 px-2.5 py-1 text-[11px] text-n-slate-11"
                >
                  {{
                    $t('CONVERSATION_SIDEBAR.VTEX.ORDERS_COUNT', {
                      count: customer.orders_count,
                    })
                  }}
                </span>
                <span
                  v-if="
                    customer.lifetime_value !== undefined &&
                    customer.lifetime_value !== null
                  "
                  class="rounded-full bg-n-alpha-2 px-2.5 py-1 text-[11px] text-n-slate-11"
                >
                  {{
                    $t('CONVERSATION_SIDEBAR.VTEX.LIFETIME_VALUE', {
                      value: formatCurrency(customer.lifetime_value),
                    })
                  }}
                </span>
                <span
                  v-if="customer.phone"
                  class="rounded-full bg-n-alpha-2 px-2.5 py-1 text-[11px] text-n-slate-11"
                >
                  {{ customer.phone }}
                </span>
                <span
                  v-if="customer.document"
                  class="rounded-full bg-n-alpha-2 px-2.5 py-1 text-[11px] text-n-slate-11"
                >
                  {{ customer.document }}
                </span>
              </div>
            </div>

            <div
              v-else
              class="mt-4 rounded-xl border border-dashed border-n-strong bg-n-alpha-2 px-4 py-5 text-sm text-n-slate-11 leading-6"
            >
              {{ $t('CONVERSATION_SIDEBAR.VTEX.NO_CUSTOMER') }}
            </div>
          </div>

          <div
            v-if="modules.draft_orders"
            class="rounded-2xl border border-n-weak bg-n-card p-3"
          >
            <div class="flex items-start justify-between gap-3">
              <div>
                <p
                  class="text-[10px] font-semibold uppercase tracking-[0.06em] text-n-slate-10"
                >
                  {{ $t('CONVERSATION_SIDEBAR.VTEX.DRAFTS_EYEBROW') }}
                </p>
                <h4 class="mt-1 text-sm font-semibold text-n-slate-12">
                  {{ $t('CONVERSATION_SIDEBAR.VTEX.DRAFTS_TITLE') }}
                </h4>
              </div>
              <Button
                type="button"
                sm
                slate
                :label="$t('CONVERSATION_SIDEBAR.VTEX.NEW_DRAFT_SHORT')"
                @click="
                  resetDraftForm();
                  openDraftComposer();
                "
              />
            </div>

            <div
              v-if="!canManageDrafts"
              class="mt-4 rounded-xl border border-dashed border-n-strong bg-n-alpha-2 px-4 py-5 text-sm text-n-slate-11 leading-6"
            >
              {{ $t('CONVERSATION_SIDEBAR.VTEX.DRAFTS_UNAVAILABLE') }}
            </div>

            <div v-else class="mt-3 flex flex-col gap-3">
              <div
                class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden"
              >
                <button
                  type="button"
                  class="w-full px-3 py-3 flex items-center justify-between gap-3 text-left"
                  @click="openDraftComposer"
                >
                  <div>
                    <p class="text-sm font-semibold text-n-slate-12">
                      {{ $t('CONVERSATION_SIDEBAR.VTEX.CART_TITLE') }}
                    </p>
                    <p class="mt-1 text-[11px] text-n-slate-11">
                      {{
                        $t('CONVERSATION_SIDEBAR.VTEX.CART_ITEMS', {
                          count: draftItemCount,
                        })
                      }}
                    </p>
                  </div>
                  <div class="text-right">
                    <p class="text-sm font-semibold text-n-slate-12">
                      {{ formatCurrency(draftTotal) }}
                    </p>
                    <p class="mt-1 text-[11px] text-n-slate-10">
                      {{ $t('CONVERSATION_SIDEBAR.VTEX.OPEN_COMPOSER') }}
                    </p>
                  </div>
                </button>
              </div>

              <div
                v-if="drafts.length"
                class="rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden"
              >
                <button
                  type="button"
                  class="w-full px-3 py-3 flex items-center justify-between gap-3 text-left"
                  @click="isDraftListOpen = !isDraftListOpen"
                >
                  <div>
                    <p class="text-sm font-semibold text-n-slate-12">
                      {{ $t('CONVERSATION_SIDEBAR.VTEX.DRAFTS_LIST_TITLE') }}
                    </p>
                    <p class="mt-1 text-[11px] text-n-slate-11">
                      {{
                        $t('CONVERSATION_SIDEBAR.VTEX.DRAFTS_LIST_SUBTITLE', {
                          count: draftCount,
                        })
                      }}
                    </p>
                  </div>
                  <span class="text-[11px] text-n-slate-10">
                    {{
                      isDraftListOpen
                        ? $t('CONVERSATION_SIDEBAR.VTEX.COLLAPSE')
                        : $t('CONVERSATION_SIDEBAR.VTEX.EXPAND')
                    }}
                  </span>
                </button>

                <div
                  v-if="isDraftListOpen"
                  class="border-t border-n-weak p-3 flex flex-col gap-3"
                >
                  <div
                    v-for="draft in drafts"
                    :key="draft.id"
                    class="rounded-xl border border-n-weak bg-n-card p-3"
                  >
                    <div class="flex items-start justify-between gap-3">
                      <div>
                        <p
                          :title="
                            $t('CONVERSATION_SIDEBAR.VTEX.DRAFT_LABEL', {
                              id: draft.id,
                            })
                          "
                          class="text-sm font-semibold text-n-slate-12 truncate"
                        >
                          {{
                            $t('CONVERSATION_SIDEBAR.VTEX.DRAFT_LABEL', {
                              id: draft.id,
                            })
                          }}
                        </p>
                        <p
                          :title="draft.status"
                          class="text-xs text-n-slate-11 mt-1 truncate"
                        >
                          {{ draft.status }}
                        </p>
                      </div>
                      <span class="text-sm font-medium text-n-slate-12">
                        {{
                          formatCurrency(
                            draft.totals?.total || draft.totals?.subtotal
                          )
                        }}
                      </span>
                    </div>

                    <div class="mt-3 grid grid-cols-2 gap-2">
                      <Button
                        type="button"
                        slate
                        :title="$t('CONVERSATION_SIDEBAR.VTEX.EDIT_DRAFT')"
                        :label="$t('CONVERSATION_SIDEBAR.VTEX.EDIT_SHORT')"
                        @click="applyDraftToForm(draft)"
                      />
                      <Button
                        v-if="draft.status === 'checkout_generated'"
                        type="button"
                        slate
                        :title="$t('CONVERSATION_SIDEBAR.VTEX.CONFIRM_ORDER')"
                        :label="$t('CONVERSATION_SIDEBAR.VTEX.CONFIRM_SHORT')"
                        :is-loading="isConfirmingDraft"
                        @click="confirmDraft(draft.id)"
                      />
                      <Button
                        type="button"
                        slate
                        :title="$t('CONVERSATION_SIDEBAR.VTEX.DELETE_DRAFT')"
                        :label="$t('CONVERSATION_SIDEBAR.VTEX.DELETE_SHORT')"
                        @click="deleteDraft(draft.id)"
                      />
                      <Button
                        v-if="draft.status === 'draft'"
                        type="button"
                        slate
                        :title="$t('CONVERSATION_SIDEBAR.VTEX.APPROVE_DRAFT')"
                        :label="$t('CONVERSATION_SIDEBAR.VTEX.CHECKOUT_SHORT')"
                        class="col-span-2"
                        @click="approveDraft(draft.id)"
                      />
                      <a
                        v-if="draft.checkout_url"
                        :href="draft.checkout_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="col-span-2 text-xs font-medium text-n-brand"
                      >
                        {{ $t('CONVERSATION_SIDEBAR.VTEX.CHECKOUT_LINK') }}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="rounded-xl border border-dashed border-n-strong bg-n-alpha-2 px-4 py-5 text-sm text-n-slate-11 leading-6"
              >
                {{ $t('CONVERSATION_SIDEBAR.VTEX.NO_DRAFTS') }}
              </div>
            </div>
          </div>

          <div
            v-if="modules.orders_sidebar"
            class="rounded-2xl border border-n-weak bg-n-card p-3"
          >
            <div class="flex items-start justify-between gap-3">
              <div>
                <p
                  class="text-[10px] font-semibold uppercase tracking-[0.06em] text-n-slate-10"
                >
                  {{ $t('CONVERSATION_SIDEBAR.VTEX.ORDERS_EYEBROW') }}
                </p>
                <h4 class="mt-1 text-sm font-semibold text-n-slate-12">
                  {{ $t('CONVERSATION_SIDEBAR.VTEX.ORDERS_TITLE') }}
                </h4>
              </div>
            </div>

            <div
              v-if="hasCustomer && orders.length"
              class="mt-4 flex flex-col gap-3"
            >
              <div class="flex items-center justify-between gap-3">
                <p class="text-xs text-n-slate-11">
                  {{
                    $t('CONVERSATION_SIDEBAR.VTEX.ORDERS_SUMMARY', {
                      count: orderCount,
                    })
                  }}
                </p>
                <button
                  v-if="orders.length > 1"
                  type="button"
                  class="text-xs font-medium text-n-brand"
                  @click="isOrdersOpen = !isOrdersOpen"
                >
                  {{
                    isOrdersOpen
                      ? $t('CONVERSATION_SIDEBAR.VTEX.SHOW_LESS')
                      : $t('CONVERSATION_SIDEBAR.VTEX.SHOW_ALL')
                  }}
                </button>
              </div>
              <VTEXOrderItem
                v-for="order in visibleOrders"
                :key="order.id"
                :order="order"
                :modules="modules"
                :capabilities="capabilities"
              />
            </div>
            <div
              v-else-if="hasCustomer"
              class="mt-4 rounded-xl border border-dashed border-n-strong bg-n-alpha-2 px-4 py-5 text-sm text-n-slate-11 leading-6"
            >
              {{ $t('CONVERSATION_SIDEBAR.VTEX.NO_ORDERS') }}
            </div>
            <div
              v-else
              class="mt-4 rounded-xl border border-dashed border-n-strong bg-n-alpha-2 px-4 py-5 text-sm text-n-slate-11 leading-6"
            >
              {{ $t('CONVERSATION_SIDEBAR.VTEX.NO_ORDERS_WITHOUT_CUSTOMER') }}
            </div>
          </div>
        </div>

        <Dialog
          ref="draftComposerDialogRef"
          width="3xl"
          position="top"
          overflow-y-auto
          :show-confirm-button="false"
          :show-cancel-button="false"
          :title="$t('CONVERSATION_SIDEBAR.VTEX.COMPOSER_TITLE')"
          :description="$t('CONVERSATION_SIDEBAR.VTEX.COMPOSER_SUBTITLE')"
        >
          <div class="flex flex-col gap-4">
            <div
              v-if="!hasCustomer"
              class="rounded-xl border border-dashed border-n-strong bg-n-alpha-2 p-4 flex flex-col gap-3"
            >
              <p class="text-sm text-n-slate-11 leading-6">
                {{ $t('CONVERSATION_SIDEBAR.VTEX.CREATE_CUSTOMER_PROMPT') }}
              </p>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  v-model="customerForm.name"
                  :label="$t('CONVERSATION_SIDEBAR.VTEX.CUSTOMER_NAME')"
                />
                <Input
                  v-model="customerForm.document"
                  :label="$t('CONVERSATION_SIDEBAR.VTEX.CUSTOMER_DOCUMENT')"
                />
                <Input
                  v-model="customerForm.email"
                  :label="$t('CONVERSATION_SIDEBAR.VTEX.CUSTOMER_EMAIL')"
                />
                <Input
                  v-model="customerForm.phone"
                  :label="$t('CONVERSATION_SIDEBAR.VTEX.CUSTOMER_PHONE')"
                />
              </div>
              <Button
                v-if="modules.customer_lookup"
                type="button"
                blue
                :label="$t('CONVERSATION_SIDEBAR.VTEX.CREATE_CUSTOMER')"
                :is-loading="isCreatingCustomer"
                @click="createCustomer"
              />
            </div>

            <div
              class="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_320px] gap-4"
            >
              <div class="flex flex-col gap-4">
                <div
                  class="rounded-xl border border-n-weak bg-n-solid-1 p-4 flex flex-col gap-3"
                >
                  <div class="flex items-end gap-2">
                    <div class="flex-1">
                      <Input
                        v-model="draftSearchQuery"
                        :label="
                          $t('CONVERSATION_SIDEBAR.VTEX.DRAFT_PRODUCTS_LABEL')
                        "
                        :placeholder="
                          $t(
                            'CONVERSATION_SIDEBAR.VTEX.DRAFT_PRODUCTS_PLACEHOLDER'
                          )
                        "
                        @enter="searchDraftProducts"
                      />
                    </div>
                    <Button
                      type="button"
                      blue
                      :label="
                        $t('CONVERSATION_SIDEBAR.VTEX.DRAFT_SEARCH_BUTTON')
                      "
                      :is-loading="isSearchingDraftProducts"
                      @click="searchDraftProducts"
                    />
                  </div>
                  <p v-if="draftSearchError" class="text-xs text-n-ruby-11">
                    {{ displayDraftSearchError }}
                  </p>
                  <div
                    v-if="draftSearchResults.length"
                    class="grid grid-cols-1 md:grid-cols-2 gap-3"
                  >
                    <div
                      v-for="product in draftSearchResults.slice(0, 6)"
                      :key="`${product.product_id}-${product.sku_id}`"
                      class="rounded-xl border border-n-weak bg-n-card p-3"
                    >
                      <div class="flex gap-3">
                        <img
                          v-if="product.image_url"
                          :src="product.image_url"
                          :alt="product.variant_name || product.name"
                          class="h-16 w-16 rounded-lg border border-n-weak object-cover shrink-0"
                        />
                        <div
                          v-else
                          class="h-16 w-16 rounded-lg border border-n-weak bg-n-alpha-2 shrink-0 flex items-center justify-center text-[11px] text-n-slate-10 text-center px-1"
                        >
                          {{ $t('INTEGRATION_SETTINGS.VTEX.SEARCH.NO_IMAGE') }}
                        </div>
                        <div class="min-w-0 flex-1">
                          <p
                            class="text-sm font-medium text-n-slate-12 break-words"
                          >
                            {{ product.variant_name || product.name }}
                          </p>
                          <p class="mt-1 text-xs text-n-slate-11">
                            {{ formatSkuLabel(product.sku_id) }}
                          </p>
                          <p class="mt-2 text-sm font-semibold text-n-slate-12">
                            {{ formatCurrency(product.price) }}
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        slate
                        :label="$t('CONVERSATION_SIDEBAR.VTEX.ADD_PRODUCT')"
                        class="mt-3 w-full"
                        @click="addDraftItem(product)"
                      />
                    </div>
                  </div>
                </div>

                <div
                  class="rounded-xl border border-n-weak bg-n-card overflow-hidden"
                >
                  <div class="border-b border-n-weak px-4 py-3">
                    <p class="text-sm font-semibold text-n-slate-12">
                      {{ $t('CONVERSATION_SIDEBAR.VTEX.DELIVERY_TITLE') }}
                    </p>
                    <p class="mt-1 text-xs text-n-slate-11">
                      {{ $t('CONVERSATION_SIDEBAR.VTEX.DELIVERY_SUBTITLE') }}
                    </p>
                  </div>
                  <div class="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input
                      v-model="draftAddress.receiver_name"
                      :label="$t('CONVERSATION_SIDEBAR.VTEX.RECEIVER_NAME')"
                    />
                    <Input
                      v-model="draftAddress.street"
                      :label="$t('CONVERSATION_SIDEBAR.VTEX.ADDRESS_STREET')"
                    />
                    <Input
                      v-model="draftAddress.number"
                      :label="$t('CONVERSATION_SIDEBAR.VTEX.ADDRESS_NUMBER')"
                    />
                    <Input
                      v-model="draftAddress.neighborhood"
                      :label="
                        $t('CONVERSATION_SIDEBAR.VTEX.ADDRESS_NEIGHBORHOOD')
                      "
                    />
                    <Input
                      v-model="draftAddress.city"
                      :label="$t('CONVERSATION_SIDEBAR.VTEX.ADDRESS_CITY')"
                    />
                    <Input
                      v-model="draftAddress.state"
                      :label="$t('CONVERSATION_SIDEBAR.VTEX.ADDRESS_STATE')"
                    />
                    <Input
                      v-model="draftAddress.postal_code"
                      :label="
                        $t('CONVERSATION_SIDEBAR.VTEX.ADDRESS_POSTAL_CODE')
                      "
                    />
                    <Input
                      v-model="draftAddress.country"
                      :label="$t('CONVERSATION_SIDEBAR.VTEX.ADDRESS_COUNTRY')"
                    />
                    <Input
                      v-model="draftLogistics.selected_sla"
                      :label="$t('CONVERSATION_SIDEBAR.VTEX.LOGISTICS_SLA')"
                    />
                    <Input
                      v-model="draftLogistics.courier"
                      :label="$t('CONVERSATION_SIDEBAR.VTEX.LOGISTICS_COURIER')"
                    />
                    <Input
                      v-model="draftLogistics.shipping_estimate"
                      :label="
                        $t('CONVERSATION_SIDEBAR.VTEX.LOGISTICS_ESTIMATE')
                      "
                    />
                    <div class="md:col-span-2">
                      <TextArea
                        v-model="draftNotes"
                        :label="$t('CONVERSATION_SIDEBAR.VTEX.DRAFT_NOTES')"
                        :placeholder="
                          $t(
                            'CONVERSATION_SIDEBAR.VTEX.DRAFT_NOTES_PLACEHOLDER'
                          )
                        "
                        :max-length="500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div class="flex flex-col gap-4">
                <div
                  class="rounded-xl border border-n-weak bg-n-card overflow-hidden"
                >
                  <div class="border-b border-n-weak px-4 py-3">
                    <div class="flex items-center justify-between gap-3">
                      <div>
                        <p class="text-sm font-semibold text-n-slate-12">
                          {{ $t('CONVERSATION_SIDEBAR.VTEX.CART_TITLE') }}
                        </p>
                        <p class="mt-1 text-xs text-n-slate-11">
                          {{
                            $t('CONVERSATION_SIDEBAR.VTEX.CART_ITEMS', {
                              count: draftItemCount,
                            })
                          }}
                        </p>
                      </div>
                      <p class="text-base font-semibold text-n-slate-12">
                        {{ formatCurrency(draftTotal) }}
                      </p>
                    </div>
                  </div>
                  <div
                    v-if="draftItems.length"
                    class="max-h-[28rem] overflow-y-auto flex flex-col divide-y divide-n-weak"
                  >
                    <div
                      v-for="item in draftItems"
                      :key="item.sku_id"
                      class="px-4 py-4"
                    >
                      <div class="flex items-start justify-between gap-3">
                        <div class="min-w-0 flex-1">
                          <p
                            class="text-sm font-medium text-n-slate-12 break-words"
                          >
                            {{ item.name }}
                          </p>
                          <p class="mt-1 text-xs text-n-slate-11">
                            {{ formatSkuLabel(item.sku_id) }}
                          </p>
                          <p class="mt-2 text-sm font-semibold text-n-slate-12">
                            {{
                              formatCurrency(
                                Number(item.price || 0) *
                                  Number(item.quantity || 0)
                              )
                            }}
                          </p>
                        </div>
                        <button
                          type="button"
                          class="text-xs font-medium text-n-ruby-11"
                          @click="removeItem(item.sku_id)"
                        >
                          {{ $t('CONVERSATION_SIDEBAR.VTEX.REMOVE_PRODUCT') }}
                        </button>
                      </div>
                      <div class="mt-3 flex items-center justify-between gap-3">
                        <div
                          class="inline-flex items-center rounded-lg border border-n-weak bg-n-alpha-2"
                        >
                          <button
                            type="button"
                            class="h-9 w-9 text-sm font-semibold text-n-slate-12"
                            @click="changeItemQuantity(item.sku_id, -1)"
                          >
                            {{ $t('CONVERSATION_SIDEBAR.VTEX.MINUS_SYMBOL') }}
                          </button>
                          <span
                            class="min-w-[2.5rem] text-center text-sm font-medium text-n-slate-12"
                          >
                            {{ item.quantity }}
                          </span>
                          <button
                            type="button"
                            class="h-9 w-9 text-sm font-semibold text-n-slate-12"
                            @click="changeItemQuantity(item.sku_id, 1)"
                          >
                            {{ $t('CONVERSATION_SIDEBAR.VTEX.PLUS_SYMBOL') }}
                          </button>
                        </div>
                        <span class="text-xs text-n-slate-11">
                          {{ formatUnitPrice(item.price) }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="px-4 py-8 text-sm text-n-slate-11">
                    {{ $t('CONVERSATION_SIDEBAR.VTEX.CART_EMPTY') }}
                  </div>
                </div>

                <div class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
                  <div class="flex items-center justify-between gap-3">
                    <div>
                      <p
                        class="text-xs uppercase tracking-[0.08em] text-n-slate-10"
                      >
                        {{ $t('CONVERSATION_SIDEBAR.VTEX.DRAFT_TOTAL') }}
                      </p>
                      <p class="mt-1 text-lg font-semibold text-n-slate-12">
                        {{ formatCurrency(draftTotal) }}
                      </p>
                    </div>
                    <div class="text-right">
                      <p
                        class="text-xs uppercase tracking-[0.08em] text-n-slate-10"
                      >
                        {{ $t('CONVERSATION_SIDEBAR.VTEX.QUANTITY') }}
                      </p>
                      <p class="mt-1 text-sm font-semibold text-n-slate-12">
                        {{ draftItemCount }}
                      </p>
                    </div>
                  </div>
                  <div class="mt-4 flex flex-col gap-2">
                    <Button
                      type="button"
                      blue
                      :label="saveDraftLabel"
                      :is-loading="isSavingDraft"
                      @click="saveDraft"
                    />
                    <Button
                      type="button"
                      slate
                      :label="$t('CONVERSATION_SIDEBAR.VTEX.APPROVE_DRAFT')"
                      :disabled="!activeDraftId"
                      :is-loading="isApprovingDraft"
                      @click="approveDraft(activeDraftId)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      </div>
    </div>
  </div>
</template>
